import { useState } from 'react'
import { bridge } from '../../bridge'
import '../ActionMenu.css'
import type { ChatMessage } from '../../store/types'
import { deleteChatMessage } from '../../api/ws'
import { EmojiPicker } from './ComposerTools'
import { myReactionEmoji } from './reactions'

// A few one-tap suggestions; the "＋" button opens the full picker to react with
// any emoji (iMessage / Signal style).
const QUICK_REACTIONS = ['❤️', '👍', '👎', '😂', '🔥']

export function MessageActionMenu({
  message,
  myUserID,
  onClose,
  onReply,
  onEdit,
  onReact,
  sendToChannel,
}: {
  message: ChatMessage
  myUserID: string
  onClose: () => void
  // Opens the message's thread to reply. Absent inside a thread (one level).
  onReply?: () => void
  onEdit: () => void
  // Toggle the viewer's reaction to `emoji`. The parent enforces one-per-person
  // (replace/clear) and owns the optimistic update + WS calls.
  onReact: (emoji: string) => void
  // "Send to #room" for one of your own thread replies that's only in the
  // thread (Slack's "Send reply to channel"). Absent when it doesn't apply.
  sendToChannel?: { label: string; onSelect: () => void }
}) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showAllEmoji, setShowAllEmoji] = useState(false)
  const isOwn = message.sender_kind === 'human' && message.sender_id === myUserID

  // The one emoji the viewer currently reacts with — highlighted in the strip.
  const myEmoji = myReactionEmoji(message.reactions, myUserID)

  function handleReaction(emoji: string) {
    onReact(emoji)
    onClose()
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    bridge.call('haptic.warning').catch(() => {})
    onClose()
    try {
      await deleteChatMessage(message.id)
    } catch {
      // Broadcast failure is non-fatal; server is source of truth.
    }
  }

  function handleReply() {
    onReply?.()
    onClose()
  }

  function handleEdit() {
    onEdit()
    onClose()
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(message.text)
      bridge.call('haptic.success').catch(() => {})
    } catch {
      // Clipboard unavailable (non-secure context in dev).
    }
    onClose()
  }

  return (
    <div className="msg-action-menu" onClick={onClose}>
      <div
        className="msg-action-menu__panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Emoji reaction strip — quick suggestions + a "＋" that opens the
            full picker to react with any emoji. */}
        <div className="msg-action-menu__reactions">
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              className={`msg-action-menu__emoji${myEmoji === emoji ? ' msg-action-menu__emoji--active' : ''}`}
              onClick={() => handleReaction(emoji)}
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}
          <button
            className={`msg-action-menu__emoji msg-action-menu__emoji--more${showAllEmoji ? ' msg-action-menu__emoji--active' : ''}`}
            onClick={() => setShowAllEmoji((v) => !v)}
            aria-label="More emoji"
            aria-expanded={showAllEmoji}
          >
            ＋
          </button>
        </div>

        {showAllEmoji && (
          <div className="msg-action-menu__emoji-full">
            <EmojiPicker inline onPick={(emoji) => handleReaction(emoji)} />
          </div>
        )}

        <div className="msg-action-menu__separator" />

        {/* Action rows */}
        {onReply && (
          <button className="msg-action-menu__action" onClick={handleReply}>
            <span className="msg-action-menu__action-icon">↩</span>
            Reply in thread
          </button>
        )}

        {sendToChannel && (
          <button
            className="msg-action-menu__action"
            onClick={() => {
              sendToChannel.onSelect()
              onClose()
            }}
          >
            <span className="msg-action-menu__action-icon">↗</span>
            {sendToChannel.label}
          </button>
        )}

        {!message.deleted_at && (
          <button className="msg-action-menu__action" onClick={() => void handleCopy()}>
            <span className="msg-action-menu__action-icon">⎘</span>
            Copy
          </button>
        )}

        {isOwn && !message.deleted_at && (
          <button className="msg-action-menu__action" onClick={handleEdit}>
            <span className="msg-action-menu__action-icon">✎</span>
            Edit
          </button>
        )}

        {isOwn && !message.deleted_at && (
          <button
            className={`msg-action-menu__action msg-action-menu__action--danger${confirmDelete ? ' msg-action-menu__action--confirming' : ''}`}
            onClick={() => void handleDelete()}
          >
            <span className="msg-action-menu__action-icon">🗑</span>
            {confirmDelete ? 'Delete?' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  )
}
